/**
 * storageImage — helpers pour servir les images Storage via le proxy
 * `render/image` (ImgProxy serveur Supabase). Permet :
 *   - Conversion HEIC → JPEG a la volee (le browser ne sait pas decoder HEIC,
 *     ImgProxy oui)
 *   - Resize / compression a la volee (eco bande passante client)
 *   - Format auto (WebP si supporte par le browser, fallback JPEG)
 *
 * Tous les buckets Cinqo (audit-photos, job-attachments, flux-photos, avatars,
 * client-assets) sont publics → on utilise le path public sans signed token.
 *
 * Usage :
 *
 *   <img src={storageImageUrl(flux.photo_url, { width: 400 })} />
 *   <a href={storageUrl(attachment.file_url)}>…</a>
 *
 *   La valeur stockee est un chemin nu `bucket/cle` : l'URL est toujours
 *   fabriquee avec VITE_SUPABASE_URL.
 *
 *   ou si on a juste le bucket et la cle separes :
 *
 *   <img src={renderImage('audit-photos', 'audit-id/photo-id.heic', { width: 320 })} />
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL

export interface RenderOpts {
  /** Largeur cible en px. Hauteur calculee proportionnellement. */
  width?: number
  /** Hauteur cible en px. */
  height?: number
  /** Format de sortie. Par defaut (`undefined`), ImgProxy choisit
   *  automatiquement (JPEG ou WebP selon Accept du browser) ET convertit
   *  les HEIC sources en JPEG decodable. Passer `'origin'` desactive
   *  l'optimisation et force le format source (deconseille pour HEIC). */
  format?: 'origin'
  /** Comportement de resize : contain, cover, fill. Défaut Supabase = cover,
   *  qui avec une seule dimension garde l'autre à sa taille d'origine et rogne
   *  (logo 811×900 en width=160 → bande 160×900). Une seule dimension sans
   *  resize → contain (proportions gardées). */
  resize?: 'contain' | 'cover' | 'fill'
}

/** Construit un URL render/image depuis un bucket + path. */
export function renderImage(bucket: string, path: string, opts: RenderOpts = {}): string {
  const params = buildParams(opts)
  return `${SUPABASE_URL}/storage/v1/render/image/public/${bucket}/${path}${params}`
}

/** Valeur stockee en base pour un fichier d'un bucket public : chemin nu
 *  `bucket/cle`, eventuellement suivi du cache-buster `?v=` (#1398). Rend le
 *  chemin et le cache-buster, ou null si la valeur n'est pas un chemin (avatar
 *  Google, `blob:`, `data:`, demo, URL de n'importe quel hote). */
function parseStorageValue(value: string): { path: string; cacheBuster: string } | null {
  // Chemin nu : ni schema (`https:`, `blob:`, `data:`), ni chemin absolu.
  if (/^[a-z][a-z0-9+.-]*:/i.test(value) || value.startsWith('/') || !value.includes('/')) return null
  const [path, query = ''] = value.split('?')
  // On ne garde que le cache-buster : les params de resize sont refaits a l'appel.
  const cacheBuster = query.split('&').filter(p => /^v=/.test(p)).join('&')
  return { path, cacheBuster }
}

/** URL du fichier, fabriquee avec l'adresse API du moment (`VITE_SUPABASE_URL`,
 *  `/sb` en prod). Pour les liens, telechargements, visualiseur PDF et `fetch`.
 *  Une valeur qui n'est pas un fichier Storage est renvoyee telle quelle. */
export function storageUrl(value: string): string
export function storageUrl(value: string | null | undefined): string | null
export function storageUrl(value: string | null | undefined): string | null {
  if (!value) return null
  const parsed = parseStorageValue(value)
  if (!parsed) return value
  const qs = parsed.cacheBuster ? `?${parsed.cacheBuster}` : ''
  return `${SUPABASE_URL}/storage/v1/object/public/${parsed.path}${qs}`
}

/** URL redimensionnee (`render/image`) du fichier, avec l'adresse API du moment.
 *  Pour les `<img>`. Une valeur qui n'est pas un fichier Storage est renvoyee
 *  telle quelle. */
export function storageImageUrl(value: string, opts?: RenderOpts): string
export function storageImageUrl(value: string | null | undefined, opts?: RenderOpts): string | null
export function storageImageUrl(value: string | null | undefined, opts: RenderOpts = {}): string | null {
  if (!value) return null
  const parsed = parseStorageValue(value)
  if (!parsed) return value
  const params = buildParams(opts)
  const qs = parsed.cacheBuster ? (params ? `${params}&${parsed.cacheBuster}` : `?${parsed.cacheBuster}`) : params
  return `${SUPABASE_URL}/storage/v1/render/image/public/${parsed.path}${qs}`
}

/** Ancien nom de `storageImageUrl`, garde pour les appelants existants. */
export function transformPublicUrl(publicUrl: string | null | undefined, opts: RenderOpts = {}): string | null {
  return storageImageUrl(publicUrl, opts)
}

function buildParams(opts: RenderOpts): string {
  const parts: string[] = []
  if (opts.width) parts.push(`width=${opts.width}`)
  if (opts.height) parts.push(`height=${opts.height}`)
  if (opts.format) parts.push(`format=${opts.format}`)
  const resize = opts.resize ?? (!opts.width !== !opts.height ? 'contain' : undefined)
  if (resize) parts.push(`resize=${resize}`)
  return parts.length > 0 ? `?${parts.join('&')}` : ''
}
